import { Injectable } from '@angular/core';
import { Observable, tap, of, delay, map } from 'rxjs';
import { ApplicationStatus, ApplicationUploadFilesInput, ClaStatus, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetClaFileInput, NpfContactDto, NpfDocumentsOutput, NpfDto, NpfPrescriptionFraudDto, NpfServiceProxy, NpfStatus, PersonApplicationSummaryDto } from '@shared/service-proxies/service-proxies';
import { ApplicationService } from '../application/application.service';
import { PermissionCheckerService } from '@node_modules/abp-ng2-module';

@Injectable({
    providedIn: 'root',
})
export class NpfService {
    npf: NpfDto;
    npfService: any;

    constructor(
        private readonly applicationService: ApplicationService,
        private readonly npfServiceProxy: NpfServiceProxy,
        private readonly permissionService: PermissionCheckerService
    ) {}

    public getNpf(caseId: string): Observable<NpfDto> {
        return this.npfServiceProxy.getNpf(caseId).pipe(
            tap((output: NpfDto) => {
                this.npf = output;
            })
        );
    }

    public closeNpf(): void {
        this.npf = this.npf;
    }

    get caseId(): string {
        return this.npf.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.npf.application.status;
    }

    public getContact(): Observable<NpfContactDto> {
        return of(this.npf.contact).pipe(
            map(x => {
                if (x === undefined) {
                    return {
                        gln: undefined,
                    } as NpfContactDto
                }
                return x;
            }),
            delay(1));
    }

    public createOrUpdateContact(dto: NpfContactDto): Observable<NpfStatus> {
        this.applicationService.setStepToReview(NpfStatus.Contact);
        return this.npfServiceProxy.createOrUpdateContact(this.caseId, dto).pipe(
            tap(result => {
                this.npf.contact = result.data;
            })
            ,map(result => {
                return result.status;
            })
        );
    }

    public getPrescriptionFraud(): Observable<NpfPrescriptionFraudDto> {
        return of(this.npf.prescriptionFraud).pipe(delay(1));
    }

    public createOrUpdatePrescriptionFraud(dto: NpfPrescriptionFraudDto): Observable<NpfStatus> {
        this.applicationService.setStepToReview(NpfStatus.PrescriptionFraud);
        return this.npfServiceProxy.createOrUpdatePrescriptionFraud(this.caseId, dto).pipe(
            tap(result => {
                this.npf.prescriptionFraud = result.data;
            })
            ,map(result => {
                return result.status;
            })
        );
    }

    public releaseNpf(comment: string): Observable<void> {
        return this.npfServiceProxy.releaseNpf(this.caseId, comment);
    }

    public checkDocuments(): Observable<NpfStatus[]> {
        return this.npfServiceProxy.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    public getEvaluationComments(): Observable<string> {
        return this.npfServiceProxy.getEvaluationComments(this.caseId);
    }

    getMyNpfs(): Observable<PersonApplicationSummaryDto[]> {
        return this.npfServiceProxy.getMyNpfs().pipe();
    }

    getStepUrl(nextStep: NpfStatus): string {
        return this.applicationService.getNextStep(NpfService.getUrlForStep(nextStep));
    }

    getNextStatus(status: NpfStatus): Observable<NpfStatus> {
        let isAuthority = this.permissionService.isGranted('Pages.Authority.Applications');
        return this.npfServiceProxy.getNextStatus(this.caseId, status).pipe(map(status => {
            if (isAuthority) {
                return NpfStatus.FinishReview;
            }
            return status;
        }));
    }
    
    static getUrlForStep(step: NpfStatus): string {
        switch (step) {
            case NpfStatus.Contact:
                return 'contact';
            case NpfStatus.PrescriptionFraud:
                return 'prescription-fraud';
            case NpfStatus.Release:
                return 'release';
            case NpfStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    public getFiles(step: NpfStatus): Observable<GeneralDocumentGroupDto[]> {
        return this.npfServiceProxy.getFiles(this.caseId).pipe(map((output: NpfDocumentsOutput) => output.documents));
    }

    public getFile(step: NpfStatus, id: number): Observable<GeneralFileDto> {
        return this.npfServiceProxy.getFile(this.caseId, NpfStatus.PrescriptionFraud, id);
    }
    
    public uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.npfServiceProxy.uploadFiles(input);
    }

    public deleteFile(step: NpfStatus, id: number): Observable<void> {
        return this.npfServiceProxy.deleteFile(this.applicationService.caseId, NpfStatus.PrescriptionFraud, id);
    }
}
